import React from "react";
import {MenuItem, TextField} from "@mui/material";
import type {GridColDef} from "@mui/x-data-grid-premium";
import {
    ArrowForwardOutlined,
    FlagOutlined,
    PaymentsOutlined,
    SavingsOutlined,
    StorefrontOutlined,
    TrendingUpOutlined,
} from "@mui/icons-material";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid";
import {Tag} from "~/components/tag/Tag";
import {SegmentedControl} from "~/components/segmented-control/SegmentedControl";
import type {SitKpiPill} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import {searchInputProps} from "~/experiments/components/search-field/searchFieldSlotProps.ts";
import {usePtV2Context} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Context.ts";
import {
    annualValue,
    BASELINE_DAYS,
    HARMONIZATION_TARGET_OPTIONS,
    harmonizationActionInput,
    harmonizationTargetLabel,
    harmonizationView,
    type PtV2HarmonizationTarget,
    type PtV2HarmonizationView,
    rateLabel,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Calc.ts";
import {formatEur, formatInt} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Format.ts";
import {usePtV2HarmonizationQuery} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";
import {PtV2TabFrame} from "~/experiments/pages/payment-terms-v2/components/PtV2TabFrame.tsx";
import {PtV2SpendBar} from "~/experiments/pages/payment-terms-v2/components/PtV2SpendBar.tsx";
import {PtV2TermTag} from "~/experiments/pages/payment-terms-v2/components/PtV2TermTag.tsx";
import {PtV2DetailDrawer} from "~/experiments/pages/payment-terms-v2/components/PtV2DetailDrawer.tsx";
import {PtV2HarmonizationDetail} from "~/experiments/pages/payment-terms-v2/components/PtV2HarmonizationDetail.tsx";
import {PtV2TermMixBar} from "~/experiments/pages/payment-terms-v2/components/PtV2TermMixBar.tsx";
import {usePtV2Period} from "~/experiments/pages/payment-terms-v2/usePtV2Period.ts";

const ALL = '';

export const HarmonizationTab: React.FC = () => {
    const {hasData, costOfCapitalPct} = usePtV2Context();
    // The workpaper's own per-supplier target by default; NT45/60/90 are the API scenarios.
    const [target, setTarget] = React.useState<PtV2HarmonizationTarget>('supplier');
    const [term, setTerm] = React.useState(ALL);
    const [tier, setTier] = React.useState(ALL);
    const [query, setQuery] = React.useState('');
    const [selected, setSelected] = React.useState<PtV2HarmonizationView | null>(null);
    const harmQ = usePtV2HarmonizationQuery(target === 'supplier' ? BASELINE_DAYS : target, hasData);
    const all = React.useMemo(() => harmonizationView(harmQ.data?.rows ?? [], target), [harmQ.data, target]);

    const terms = React.useMemo(() => [...new Set(all.map(r => r.currentTerm))].sort(), [all]);
    const tiers = React.useMemo(
        () => [...new Set(all.map(r => r.negotiationTier).filter((t): t is string => Boolean(t)))].sort(),
        [all],
    );
    const period = usePtV2Period('harmonization');
    const q = query.trim().toLowerCase();
    const rows = all
        .filter(r => (term === ALL || r.currentTerm === term)
            && (tier === ALL || r.negotiationTier === tier)
            && (!q || r.supplier.toLowerCase().includes(q)))
        .sort((a, b) => b.wcFreed - a.wcFreed);
    const maxWc = Math.max(1, ...all.map(r => r.wcFreed));
    const totalWc = target === 'supplier' ? all.reduce((s, r) => s + r.wcFreed, 0) : harmQ.data?.totalWc;
    const targetLabel = harmonizationTargetLabel(target);

    const kpis: SitKpiPill[] = [
        {tone: 'ok', icon: <SavingsOutlined/>, label: 'Working capital freed', value: formatEur(totalWc)},
        ...(costOfCapitalPct != null ? [{tone: 'ok' as const, icon: <TrendingUpOutlined/>, label: 'Annual value',
            value: formatEur(annualValue(totalWc, costOfCapitalPct)),
            help: `Working capital freed × ${rateLabel(costOfCapitalPct)} cost of capital (Payment Terms settings).`}] : []),
        {tone: 'plain', icon: <PaymentsOutlined/>, label: 'Spend in scope', value: formatEur(all.reduce((s, r) => s + r.spend, 0))},
        {tone: 'plain', icon: <StorefrontOutlined/>, label: 'Suppliers moving', value: formatInt(all.filter(r => r.shiftDays > 0).length)},
        {tone: 'info', icon: <FlagOutlined/>, label: 'Target term', value: targetLabel},
    ];

    const columns: GridColDef<PtV2HarmonizationView>[] = [
        {field: 'supplier', headerName: 'Supplier', flex: 1.4, minWidth: 170,
            renderCell: p => <span className="ptv2-strong ptv2-ell" title={p.row.supplier}>{p.row.supplier}</span>},
        {field: 'category', headerName: 'Category', flex: 1, minWidth: 120,
            renderCell: p => p.row.category
                ? <span className="ptv2-muted ptv2-ell" title={p.row.category}>{p.row.category}</span>
                : <span className="ptv2-faint">Not stated</span>},
        {field: 'targetTerm', headerName: 'Target', width: 150,
            description: 'Current term → the target: the workpaper’s own per supplier, or the term chosen above for every supplier.',
            valueGetter: (_v, row) => row.targetCode ?? row.currentTerm,
            renderCell: p => <span className="ptv2-chips">
                <PtV2TermTag code={p.row.currentTerm}/>
                {p.row.targetCode && <>
                    <ArrowForwardOutlined sx={{fontSize: 14}} className="ptv2-faint"/>
                    <PtV2TermTag code={p.row.targetCode}/>
                </>}
            </span>},
        {field: 'negotiationTier', headerName: 'Tier', width: 84,
            renderCell: p => p.row.negotiationTier
                ? <Tag color="default" variant="outlined" label={p.row.negotiationTier}/>
                : <span className="ptv2-faint">—</span>},
        {field: 'termMix', headerName: 'Term mix', width: 140, sortable: false,
            renderCell: p => <PtV2TermMixBar mix={p.row.termMix ?? []}/>},
        {field: 'shiftDays', headerName: 'DPO gain', width: 96, type: 'number',
            renderCell: p => p.row.shiftDays > 0
                ? <span className="ptv2-good ptv2-num">+{p.row.shiftDays} days</span>
                : <span className="ptv2-faint">None</span>},
        {field: 'invoiceCount', headerName: 'Invoices', width: 84, type: 'number',
            renderCell: p => <span className="ptv2-num">{formatInt(p.row.invoiceCount)}</span>},
        {field: 'spend', headerName: 'Spend', width: 96, type: 'number',
            renderCell: p => <span className="ptv2-num">{formatEur(p.row.spend)}</span>},
        {field: 'wcFreed', headerName: 'Working capital freed', flex: 1, minWidth: 170, type: 'number',
            renderCell: p => <PtV2SpendBar ratio={p.row.wcFreed / maxWc} color="var(--ptv2-good)">
                {formatEur(p.row.wcFreed)}
            </PtV2SpendBar>},
    ];

    return <PtV2TabFrame
        tab="harmonization"
        kpis={kpis}
        kpiAside={period}
        loading={harmQ.isPending}
        error={harmQ.isError}
        summary={<>The highest-yield suppliers paid faster than the corporate baseline. Moving them to{' '}
            <b>{target === 'supplier' ? 'their workpaper target' : targetLabel}</b> frees <b>{formatEur(totalWc)}</b> of working capital for good.</>}>
        <div className="ptv2-filterbar">
            <div className="ptv2-field">
                <span className="ptv2-eyebrow">Target</span>
                <SegmentedControl
                    sx={{width: 320}}
                    labels={HARMONIZATION_TARGET_OPTIONS.map(harmonizationTargetLabel)}
                    value={HARMONIZATION_TARGET_OPTIONS.indexOf(target)}
                    onChange={i => {
                        setTarget(HARMONIZATION_TARGET_OPTIONS[i]);
                        setSelected(null);
                    }}/>
            </div>
            <TextField select size="small" label="Current term" value={term} sx={{minWidth: 160}}
                       onChange={e => setTerm(e.target.value)}>
                <MenuItem value={ALL}>All terms</MenuItem>
                {terms.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            {tiers.length > 0 && <TextField select size="small" label="Tier" value={tier} sx={{minWidth: 140}}
                                            onChange={e => setTier(e.target.value)}>
                <MenuItem value={ALL}>All tiers</MenuItem>
                {tiers.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>}
            <TextField type="search" size="small" placeholder="Search suppliers" value={query}
                       onChange={e => setQuery(e.target.value)} sx={{minWidth: 240}}
                       slotProps={{htmlInput: searchInputProps('ptv2-harmonization-search', 'Search suppliers')}}/>
        </div>
        <div className="ptv2-section">
            <div className="ptv2-grid-card">
                <CustomDataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={r => r.supplierKey}
                    onRowClick={p => setSelected(p.row as PtV2HarmonizationView)}
                    autoHeight
                    hideFooter
                    disableRowSelectionOnClick
                    disableColumnMenu
                    className="ptv2-clickable-rows"
                    localeText={{noRowsLabel: 'No suppliers match the filters or search'}}
                />
            </div>
            <p className="ptv2-foot">
                {target === 'supplier'
                    ? <>Per supplier: the workpaper’s target term and liquidity unlock (else spend × (target days −
                        current days) ÷ 365).</>
                    : <>Freed working capital = spend × (target days − current days) ÷ 365.</>}{' '}
                {costOfCapitalPct != null && <>Annual value = freed working capital × {rateLabel(costOfCapitalPct)} cost of capital.{' '}</>}
                Suppliers already at or above the target show no gain.
            </p>
        </div>
        <PtV2DetailDrawer
            open={!!selected}
            title={selected?.supplier ?? ''}
            subtitle={`Term harmonization · target ${selected?.targetCode ?? 'not set'}`}
            onClose={() => setSelected(null)}
            action={selected?.targetCode && selected.shiftDays > 0 ? harmonizationActionInput(selected, selected.targetCode) : null}>
            {selected && <PtV2HarmonizationDetail row={selected}/>}
        </PtV2DetailDrawer>
    </PtV2TabFrame>;
};
